import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Check, Loader2, AlertCircle, ShieldCheck } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { cn } from '@/lib/utils'
import { supabase } from '@/lib/supabase'
import { useAuthStore } from '@/stores/auth-store'
import { ChangePasswordFormSchema, type ChangePasswordForm } from '@/lib/schemas'
import { MfaEnrollDialog } from '@/components/settings/MfaEnrollDialog'
import { MfaDisableDialog } from '@/components/settings/MfaDisableDialog'
import { MfaRecoveryCodesDisplay } from '@/components/settings/MfaRecoveryCodesDisplay'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { mfaListFactors, mfaChallenge, mfaVerify } from '@/lib/supabase'
import { api } from '@/lib/api'
import { captureEvent } from '@/lib/posthog'

interface RegenerateCodesResponse {
  codes: string[]
  remaining: number
}

function MfaRegenerateCodesDialog({
  open,
  onOpenChange,
  email,
  onComplete,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  email: string
  onComplete: (remaining: number) => void
}) {
  const [step, setStep] = useState<'verify' | 'codes'>('verify')
  const [code, setCode] = useState('')
  const [codes, setCodes] = useState<string[]>([])
  const [error, setError] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)

  const reset = () => {
    setStep('verify')
    setCode('')
    setCodes([])
    setError(null)
    setIsLoading(false)
  }

  const handleVerifyAndRegenerate = async () => {
    if (code.length !== 6) return
    setIsLoading(true)
    setError(null)
    try {
      const factors = await mfaListFactors()
      const totpFactor = (factors?.all ?? [])
        .filter((f) => f.factor_type === 'totp')
        .find((f) => f.status === 'verified')
      if (!totpFactor) throw new Error('No verified authenticator found')
      const challenge = await mfaChallenge(totpFactor.id)
      await mfaVerify(totpFactor.id, challenge.id, code)
      const result = await api.post<RegenerateCodesResponse>('/users/me/mfa-recovery-codes', {})
      setCodes(result.codes)
      setStep('codes')
      captureEvent('mfa_recovery_regenerated')
      onComplete(result.remaining)
    } catch (e) {
      setError(
        e instanceof Error
          ? e.message
          : "That code wasn't valid. Try again with the next code your app shows."
      )
      setCode('')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        onOpenChange(o)
        if (!o) reset()
      }}
    >
      <DialogContent className="sm:max-w-md">
        {step === 'verify' && (
          <>
            <DialogHeader>
              <DialogTitle>Generate new recovery codes</DialogTitle>
              <DialogDescription>
                Enter the 6-digit code from your authenticator app to confirm. Your existing
                recovery codes will stop working.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div className="space-y-2">
                <Label className="text-meta">Verification code</Label>
                <Input
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  pattern="[0-9]{6}"
                  maxLength={6}
                  value={code}
                  onChange={(e) => {
                    const digits = e.target.value.replace(/\D/g, '').slice(0, 6)
                    setCode(digits)
                    if (digits.length === 6 && !isLoading) {
                      void handleVerifyAndRegenerate()
                    }
                  }}
                  className="text-center text-lg tracking-widest font-mono"
                  autoFocus
                />
              </div>
              {error && (
                <div className="flex items-center gap-2 p-3 rounded-lg border border-destructive/30 bg-destructive/10 text-sm text-destructive">
                  <AlertCircle className="h-4 w-4 flex-shrink-0" />
                  <span>{error}</span>
                </div>
              )}
              <div className="flex justify-end gap-2">
                <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
                  Cancel
                </Button>
                <Button
                  type="button"
                  onClick={() => void handleVerifyAndRegenerate()}
                  disabled={code.length !== 6 || isLoading}
                >
                  {isLoading && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                  Generate codes
                </Button>
              </div>
            </div>
          </>
        )}
        {step === 'codes' && (
          <>
            <DialogHeader>
              <DialogTitle>Save your new recovery codes</DialogTitle>
            </DialogHeader>
            <MfaRecoveryCodesDisplay
              codes={codes}
              email={email}
              onConfirm={() => {
                onOpenChange(false)
                reset()
                toast.success('New recovery codes generated')
              }}
            />
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}

export function AccountSecurityTab() {
  const user = useAuthStore((state) => state.user)
  const mfaEnrolled = useAuthStore((s) => s.mfaEnrolled)
  const mfaEnrolledAt = useAuthStore((s) => s.mfaEnrolledAt)
  const mfaRequired = useAuthStore((s) => s.mfaRequired)
  const recoveryCodesRemaining = useAuthStore((s) => s.recoveryCodesRemaining)
  const setMfaStatus = useAuthStore((s) => s.setMfaStatus)
  const updateDisplayName = useAuthStore((s) => s.updateDisplayName)
  const currentDisplayName = (user?.user_metadata?.['full_name'] as string | undefined) ?? ''
  const [displayName, setDisplayName] = useState(currentDisplayName)
  const [isSavingName, setIsSavingName] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)
  const [enrollOpen, setEnrollOpen] = useState(false)
  const [disableOpen, setDisableOpen] = useState(false)
  const [regenerateOpen, setRegenerateOpen] = useState(false)

  const form = useForm<ChangePasswordForm>({
    resolver: zodResolver(ChangePasswordFormSchema),
    defaultValues: { currentPassword: '', newPassword: '', confirmPassword: '' },
  })

  const {
    register,
    formState: { errors },
    reset,
  } = form

  const onSubmit = async (data: ChangePasswordForm) => {
    setIsLoading(true)
    setError(null)
    setSuccess(false)

    try {
      // Verify current password by attempting to sign in
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: user?.email ?? '',
        password: data.currentPassword,
      })

      if (signInError) {
        setError('Current password is incorrect')
        setIsLoading(false)
        return
      }

      // Update to new password
      const { error: updateError } = await supabase.auth.updateUser({
        password: data.newPassword,
      })

      if (updateError) throw updateError

      setSuccess(true)
      reset()
      toast.success('Password updated successfully')
      setTimeout(() => setSuccess(false), 3000)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update password')
    } finally {
      setIsLoading(false)
    }
  }

  const handleSaveName = async () => {
    const trimmed = displayName.trim()
    if (!trimmed || trimmed === currentDisplayName) return

    setIsSavingName(true)
    try {
      await updateDisplayName(trimmed)
      toast.success('Name updated')
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to update name')
    } finally {
      setIsSavingName(false)
    }
  }

  return (
    <div className="space-y-6">
      <section className="space-y-4">
        <h2 className="text-sm font-medium">Your profile</h2>
        <p className="text-meta">
          This is the name teammates see on your comments, assignments and mentions.
        </p>
        <div className="grid gap-3 md:grid-cols-[320px_auto] md:items-end">
          <div className="space-y-1">
            <Label htmlFor="display-name" className="text-meta">
              Display name
            </Label>
            <Input
              id="display-name"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="Your name"
              disabled={isSavingName}
            />
          </div>
          <Button
            size="sm"
            className="justify-self-start"
            onClick={() => void handleSaveName()}
            disabled={isSavingName || !displayName.trim() || displayName.trim() === currentDisplayName}
          >
            {isSavingName && <Loader2 className="h-4 w-4 animate-spin" />}
            Save name
          </Button>
        </div>
      </section>

      <section className="space-y-4">
        <h2 className="text-sm font-medium">Change Password</h2>
        <p className="text-meta">
          Update the password you use to sign in to BidContext.
        </p>

        <form onSubmit={(e) => void form.handleSubmit(onSubmit)(e)} className="space-y-4 max-w-md">
          {error && (
            <div className="flex items-center gap-2 p-3 rounded-lg border border-destructive/30 bg-destructive/10 text-sm text-destructive">
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="currentPassword" className="text-meta">
              Current Password
            </Label>
            <Input
              id="currentPassword"
              type="password"
              autoComplete="current-password"
              {...register('currentPassword')}
              className={cn(errors.currentPassword && 'border-destructive')}
            />
            {errors.currentPassword && (
              <p className="text-xs text-destructive">{errors.currentPassword.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="newPassword" className="text-meta">
              New Password
            </Label>
            <Input
              id="newPassword"
              type="password"
              autoComplete="new-password"
              {...register('newPassword')}
              className={cn(errors.newPassword && 'border-destructive')}
            />
            {errors.newPassword && (
              <p className="text-xs text-destructive">{errors.newPassword.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="confirmPassword" className="text-meta">
              Confirm New Password
            </Label>
            <Input
              id="confirmPassword"
              type="password"
              autoComplete="new-password"
              {...register('confirmPassword')}
              className={cn(errors.confirmPassword && 'border-destructive')}
            />
            {errors.confirmPassword && (
              <p className="text-xs text-destructive">{errors.confirmPassword.message}</p>
            )}
          </div>

          <div className="flex justify-end pt-2">
            <Button type="submit" disabled={isLoading}>
              {isLoading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Updating...
                </>
              ) : success ? (
                <>
                  <Check className="h-4 w-4 mr-2" />
                  Updated
                </>
              ) : (
                'Update Password'
              )}
            </Button>
          </div>
        </form>
      </section>

      <section className="space-y-4">
        <div className="flex items-center gap-2">
          <h2 className="text-sm font-medium">Two-factor authentication</h2>
          {mfaEnrolled && <Badge variant="success">Enabled</Badge>}
        </div>

        {mfaEnrolled ? (
          <div className="space-y-3 max-w-md">
            <div className="flex items-start gap-2 text-sm">
              <ShieldCheck className="h-4 w-4 text-muted-foreground mt-0.5" />
              <div className="flex-1">
                <p>
                  Authenticator app enrolled
                  {mfaEnrolledAt && (
                    <> on {new Date(mfaEnrolledAt).toLocaleDateString('en-AU', {
                      day: 'numeric',
                      month: 'short',
                      year: 'numeric',
                    })}</>
                  )}
                </p>
                <p className="text-meta mt-1">
                  {recoveryCodesRemaining} recovery code{recoveryCodesRemaining === 1 ? '' : 's'} remaining
                  {recoveryCodesRemaining <= 3 && recoveryCodesRemaining > 0 && (
                    <span className="text-[hsl(var(--status-warning))] ml-1">— consider generating new ones</span>
                  )}
                </p>
              </div>
            </div>
            <div className="flex gap-2">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setRegenerateOpen(true)}
              >
                Generate new recovery codes
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="hover:text-destructive"
                disabled={mfaRequired}
                title={mfaRequired ? 'Required for your role' : undefined}
                onClick={() => setDisableOpen(true)}
              >
                Disable two-factor authentication
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-3 max-w-md">
            <p className="text-meta">
              Add an extra layer of security to your account. You'll enter a 6-digit code from
              an authenticator app each time you sign in from a new device.
            </p>
            <Button type="button" onClick={() => setEnrollOpen(true)}>
              Enable two-factor authentication
            </Button>
          </div>
        )}
      </section>

      <MfaEnrollDialog open={enrollOpen} onOpenChange={setEnrollOpen} />
      <MfaDisableDialog open={disableOpen} onOpenChange={setDisableOpen} />
      <MfaRegenerateCodesDialog
        open={regenerateOpen}
        onOpenChange={setRegenerateOpen}
        email={user?.email ?? ''}
        onComplete={(remaining) =>
          setMfaStatus({
            enrolled: true,
            enrolledAt: mfaEnrolledAt,
            recoveryCodesRemaining: remaining,
            required: mfaRequired,
          })
        }
      />
    </div>
  )
}
